import {
  AmbientLight,
  BackSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  PerspectiveCamera,
  Scene,
  SphereGeometry,
  WebGLRenderer,
} from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import type { TextureManager } from "./textures";

const STAR_RADIUS = 1e5;
const CAMERA_FAR = 1e7;

export interface RendererHandle {
  renderer: WebGLRenderer;
  scene: Scene;
  world: Group;
  camera: PerspectiveCamera;
  controls: OrbitControls;
  stars: Mesh;
  resize(): void;
  render(): void;
}

export function createRenderer(
  canvas: HTMLCanvasElement,
  textures: TextureManager,
): RendererHandle {
  const renderer = new WebGLRenderer({ canvas, antialias: true, logarithmicDepthBuffer: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  textures.maxAnisotropy = renderer.capabilities.getMaxAnisotropy();

  const scene = new Scene();
  // Sim axes are ecliptic Z-up; rotate so ecliptic north is Three.js +Y.
  const world = new Group();
  world.rotation.x = -Math.PI / 2;
  scene.add(world);
  scene.add(new AmbientLight(0xffffff, 0.08));

  const camera = new PerspectiveCamera(50, 1, 1e-4, CAMERA_FAR);
  camera.position.set(0, 600, 1000);
  const controls = new OrbitControls(camera, canvas);
  // View.frame sets dampingFactor from the frame time
  controls.enableDamping = true;
  controls.zoomSpeed = 1.2;

  const starMaterial = new MeshBasicMaterial({
    color: 0x404040,
    side: BackSide,
    depthWrite: false,
  });
  const stars = new Mesh(new SphereGeometry(STAR_RADIUS, 48, 32), starMaterial);
  stars.rotation.x = -Math.PI / 2;
  stars.renderOrder = -1;
  scene.add(stars);
  textures.load("2k_stars_milky_way.jpg", (t) => {
    starMaterial.map = t;
    starMaterial.color.set(0xffffff);
    starMaterial.needsUpdate = true;
  });

  const resize = (): void => {
    const w = canvas.clientWidth || window.innerWidth;
    const h = canvas.clientHeight || window.innerHeight;
    renderer.setSize(w, h, false);
    camera.aspect = h === 0 ? 1 : w / h;
    camera.updateProjectionMatrix();
  };
  window.addEventListener("resize", resize);
  resize();

  const render = (): void => {
    stars.position.copy(camera.position);
    renderer.render(scene, camera);
  };

  return { renderer, scene, world, camera, controls, stars, resize, render };
}
